// Code-based TanStack Router tree. The root route renders the app shell
// (slim header + content Outlet). Every route is reachable to anyone Boot() let
// through, and the ONE route whose data needs more than that (`/permissions`,
// which needs `bi_site` at `full`) is gated by the SERVER and renders the 403 as
// an explanation. A client-side route guard would be a second, weaker copy of a
// rule the API already enforces.
import {
  createRootRoute,
  createRoute,
  createRouter,
  retainSearchParams,
} from "@tanstack/react-router";

import { validateCatalogSearch } from "./lib/preview-catalog";
import { AppShell } from "./components/app-shell";
import { EmptyState } from "./components/states";
import { AskPage } from "./pages/ask";
import { CatalogPage } from "./pages/catalog";
import { DashboardPage } from "./pages/dashboard";
import { DocumentPage } from "./pages/document";
import { CorpusPage } from "./pages/corpus";
import { ExperimentDashboardPage } from "./pages/experiment-dashboard";
import { ExperimentPage, ExperimentsPage } from "./pages/experiments";
import { StudioListPage } from "./pages/studio-list";
import { StudioPage } from "./pages/studio-canvas";
import { PermissionsPage } from "./pages/permissions";
import { ProjectTagsPage } from "./pages/project-tags";
import { TaggerPage } from "./pages/tagger";
import { TaggersPage } from "./pages/taggers";

// `?catalog=pr-<n>` is declared on the ROOT route and RETAINED across every
// navigation. TanStack validates search per route and drops what no matched route
// declares, so the root declaration lets a preview selection survive into
// `/d/$slug` without every child route declaring it; retention keeps the URL an
// analyst copies pointing at the preview they see. Leaving a preview is
// deliberately NOT a retained navigation: the banner does a full page load,
// because the query cache and the captured key are both per-load
// (lib/preview-catalog.ts).
//
// A root `validateSearch` strips on navigation any query param no matched route
// declares. Nothing in this app reads an undeclared param. `lib/return-to.ts`
// reads `window.location`, not the router, so it keeps the whole string
// (`?catalog=` included) across sign-in.
const rootRoute = createRootRoute({
  component: AppShell,
  validateSearch: validateCatalogSearch,
  search: { middlewares: [retainSearchParams(["catalog"])] },
  notFoundComponent: () => (
    <div className="bi-page">
      <EmptyState title="Page not found" message="That page doesn't exist." />
    </div>
  ),
});

const catalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: CatalogPage,
});

// `slug`, not `id`: bookmarkable, matches the vendored Superset export's own
// slugs. See pages/dashboard.tsx for how it resolves to the id the backend
// route takes. `?filters=` is the SHAREABLE SNAPSHOT of the live filter store
// (lib/filter-store.ts); see that page for the seed/write-back logic.
const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/d/$slug",
  validateSearch: (search: Record<string, unknown>): { filters?: string } => ({
    filters: typeof search.filters === "string" ? search.filters : undefined,
  }),
  component: function DashboardRouteComponent() {
    const { slug } = dashboardRoute.useParams();
    const search = dashboardRoute.useSearch();
    return <DashboardPage slug={slug} initialFiltersParam={search.filters} />;
  },
});

// A DOCUMENT, not a dashboard. `/a/` rather than `/d/` because the two resolve
// through different machinery — `/d/` compiles a chart spec per tile, `/a/`
// serves one authored page whole — and a reader who bookmarks one should not
// silently get the other if a slug is ever reused.
const documentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/a/$slug",
  component: function DocumentRouteComponent() {
    const { slug } = documentRoute.useParams();
    return <DocumentPage slug={slug} />;
  },
});

// The tagging console. Same origin, same identity, different data path: the
// dashboards read the WAREHOUSE (recompiled hourly), these read the LIVE app
// database, because a run that finished a minute ago has to be visible.
//
// There is no per-route gate here either. `bi_site` at `read` reaches every one
// of these pages; the WRITE half (editing a tag, starting a run) is refused by
// the backend on `bi_site` at `full`, and the pages ask `/api/bi/tagging/me`
// so they can decline to draw a button the caller would only be refused.
const taggersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/taggers",
  component: TaggersPage,
});

const taggerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/taggers/$name",
  component: function TaggerRouteComponent() {
    const { name } = taggerRoute.useParams();
    return <TaggerPage name={name} />;
  },
});

const corpusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/corpus",
  component: CorpusPage,
});

// `?thread=` rather than a path segment: a conversation is a VIEW of the one
// Ask page (the thread rail stays put, the composer keeps its draft), not a
// different page, and a reader who copies the URL should land back in the same
// conversation.
const askRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ask",
  validateSearch: (search: Record<string, unknown>): { thread?: string } => ({
    thread: typeof search.thread === "string" ? search.thread : undefined,
  }),
  component: function AskRouteComponent() {
    const search = askRoute.useSearch();
    return <AskPage threadId={search.thread ?? null} />;
  },
});

const projectTagsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/tags",
  component: function ProjectTagsRouteComponent() {
    const { projectId } = projectTagsRoute.useParams();
    return <ProjectTagsPage projectId={projectId} />;
  },
});

// ── the studio ─────────────────────────────────────────────────────────────
//
// `/studio` lists; `/studio/new` opens an empty canvas; `/studio/$id` VIEWS a
// saved dashboard and `?edit=1` opens the editor on it for its owner.
//
// Edit-as-a-search-param rather than as a `/studio/$id/edit` segment, and the
// reason is the same one the dashboard route's `?filters=` has: it is a MODE of
// one page rather than a different page. The document, its version and the
// unsaved edits are the same in both, so a separate route would either remount
// the editor (dropping unsaved work on a preview toggle) or need the state
// hoisted somewhere both routes could reach — and the URL is already that
// somewhere.
const studioListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/studio",
  component: StudioListPage,
});

// `/studio/new` takes three optional search params, and all three are OPENING
// state rather than settings: `experiment` seeds a whole starter dashboard
// scoped to that key, `source` picks the source the rail opens on, and `field`
// names a column to bring straight into the first tile. They are what makes an
// experiment page and the search box able to hand the canvas somewhere to
// start, instead of dropping somebody on a blank grid holding the question they
// arrived with.
const studioNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/studio/new",
  validateSearch: (
    search: Record<string, unknown>,
  ): { experiment?: string; source?: string; field?: string } => ({
    experiment: typeof search.experiment === "string" ? search.experiment : undefined,
    source: typeof search.source === "string" ? search.source : undefined,
    field: typeof search.field === "string" ? search.field : undefined,
  }),
  component: function StudioNewRouteComponent() {
    const { experiment, source, field } = studioNewRoute.useSearch();
    return (
      <StudioPage
        dashboardId={null}
        edit
        seedExperiment={experiment ?? null}
        seedSource={source ?? null}
        seedField={field ?? null}
      />
    );
  },
});

const studioDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/studio/$id",
  // `edit` is validated to the literal "1" rather than coerced from anything
  // truthy: a search param is reader-supplied, and "open the editor" is a
  // decision the OWNER check backs up server-side anyway. One spelling keeps
  // the URL somebody shares unambiguous.
  //
  // `experiment` REBINDS a saved board to another experiment for this reading.
  // It is the link form of the picker in the save bar, which is what makes a
  // dashboard built for one experiment reusable for the next without a copy:
  // the URL carries the key, the parameter binds it, and the tiles are the same
  // tiles.
  validateSearch: (
    search: Record<string, unknown>,
  ): { edit?: "1"; experiment?: string } => ({
    ...(search.edit === "1" ? { edit: "1" as const } : {}),
    ...(typeof search.experiment === "string" ? { experiment: search.experiment } : {}),
  }),
  component: function StudioDashboardRouteComponent() {
    const { id } = studioDashboardRoute.useParams();
    const { edit, experiment } = studioDashboardRoute.useSearch();
    return (
      <StudioPage dashboardId={id} edit={edit === "1"} seedExperiment={experiment ?? null} />
    );
  },
});

// The register, and one experiment. `/experiments` is the list somebody scans
// before opening anything; `/experiments/$key` is where a dashboard for that
// experiment is started, and where the ones already filed under it are listed.
const experimentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/experiments",
  component: ExperimentsPage,
});

const experimentDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/experiments/$experimentKey",
  component: function ExperimentDetailRouteComponent() {
    const { experimentKey } = experimentDetailRoute.useParams();
    return <ExperimentPage experimentKey={experimentKey} />;
  },
});

// `/x/<key>`, deliberately short: this is the link that goes in a Slack message
// beside an experiment's name, and it is the ONE route here that takes a
// product key rather than a dashboard id. It saves nothing — the template is
// read under a parameter, and Customise forks it into a dashboard of the
// reader's own.
const experimentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/x/$experimentKey",
  component: function ExperimentRouteComponent() {
    const { experimentKey } = experimentRoute.useParams();
    return <ExperimentDashboardPage experimentKey={experimentKey} />;
  },
});

// The access list. Reachable by URL for everyone, readable only by a `full`
// holder — see the page for why the 403 is rendered as an answer rather than an
// error, and the app shell for why the nav entry is conditional while the route
// is not.
const permissionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/permissions",
  component: PermissionsPage,
});

const routeTree = rootRoute.addChildren([
  studioListRoute,
  studioNewRoute,
  studioDashboardRoute,
  experimentsRoute,
  experimentDetailRoute,
  experimentRoute,
  catalogRoute,
  dashboardRoute,
  taggersRoute,
  taggerRoute,
  corpusRoute,
  askRoute,
  projectTagsRoute,
  documentRoute,
  permissionsRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
